import { useState } from 'react';
import { Alert, Button, TextInput } from '@gravity-ui/uikit';
import { useSessionStore } from '../../lib/session/context.js';
import { EntryPage } from '../../ui/entry-page.js';
import { Field } from '../../ui/field.js';

/**
 * Новый пароль по ссылке из письма. Адрес почты не спрашивается: он известен
 * из ссылки, а поле на экране означало бы, что чужую ссылку можно применить к
 * своей учётной записи. После смены все прежние входы закрыты.
 */

const MIN_LENGTH = 12;

export function PasswordResetScreen({ token, onDone }: { token: string; onDone: () => void }) {
  const store = useSessionStore();
  const [password, setPassword] = useState('');
  const [repeat, setRepeat] = useState('');
  const [problem, setProblem] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  async function submit(): Promise<void> {
    if (password.length < MIN_LENGTH) {
      setProblem(`Пароль не короче ${String(MIN_LENGTH)} символов`);
      return;
    }
    if (password !== repeat) {
      setProblem('Пароли не совпадают');
      return;
    }
    setProblem(null);
    setBusy(true);
    try {
      await store.confirmPasswordReset(token, password);
      setDone(true);
    } catch (error) {
      setProblem(error instanceof Error ? error.message : 'Ссылка недействительна');
    } finally {
      setBusy(false);
    }
  }

  return (
    <EntryPage title="Новый пароль" product="Админка платформы">
      {done ? (
        <div className="flex flex-col gap-4">
          <Alert
            theme="success"
            message="Пароль изменён, прежние входы закрыты. Войдите с новым паролем и кодом из приложения-аутентификатора."
          />
          <Button view="action" size="l" width="max" onClick={onDone}>
            Войти
          </Button>
        </div>
      ) : (
        <form
          className="flex flex-col gap-4"
          onSubmit={(event) => {
            event.preventDefault();
            void submit();
          }}
        >
          <Field label="Новый пароль" hint={`Не короче ${String(MIN_LENGTH)} символов`} required>
            {(id) => (
              <TextInput
                id={id}
                size="l"
                type="password"
                autoComplete="new-password"
                disabled={busy}
                value={password}
                onUpdate={setPassword}
                autoFocus
              />
            )}
          </Field>
          <Field label="Ещё раз" required>
            {(id) => (
              <TextInput id={id} size="l" type="password" autoComplete="new-password" disabled={busy} value={repeat} onUpdate={setRepeat} />
            )}
          </Field>
          {problem === null ? null : <Alert theme="danger" message={problem} />}
          <Button type="submit" view="action" size="l" width="max" loading={busy}>
            Сменить пароль
          </Button>
        </form>
      )}
    </EntryPage>
  );
}
